
import {
  Component,
  inject,
  OnInit,
  ChangeDetectorRef,
  PLATFORM_ID
} from '@angular/core';

import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

import {
  CommonModule,
  isPlatformBrowser
} from '@angular/common';

import { FormsModule } from '@angular/forms';
import Swal from 'sweetalert2';

import {
  ParceirosService,
  Parceiro,
  ParceiroPayload,
} from '../../../core/services/parceiros.service';

type ParceiroForm = {
  nome: string;
  cpfCnpj: string;
  telefone: string;
  limiteUsuarios: number;
  emailAcesso: string;
  rua: string;
  cidade: string;
  estado: string;
  cep: string;
  pais: string;
};

type EstadoIBGE = {
  id: number;
  sigla: string;
  nome: string;
};

type CidadeIBGE = {
  id: number;
  nome: string;
};

type EnderecoViaCEP = {
  cep: string;
  logradouro: string;
  localidade: string;
  uf: string;
  erro?: boolean;
};

@Component({
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './parceiros.html',
  styleUrl: './parceiros.css',
})
export class Parceiros implements OnInit {
  private readonly api = inject(ParceirosService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly cdr = inject(ChangeDetectorRef);
  readonly padraoCpfCnpj =
    '(\\d{3}\\.\\d{3}\\.\\d{3}-\\d{2}|\\d{2}\\.\\d{3}\\.\\d{3}/\\d{4}-\\d{2})';

  items: Parceiro[] = [];

  private readonly http = inject(HttpClient);

  estados: EstadoIBGE[] = [];
  cidades: CidadeIBGE[] = [];

  carregandoEstados = false;
  carregandoCidades = false;
  buscandoCep = false;

  paises = [{ codigo: 'BR', nome: 'Brasil' }];

  erro = '';
  sucesso = '';

  salvando = false;
  carregando = false;
  excluindo = false;

  editando: string | null = null;
  mostrar = false;

  form: ParceiroForm = this.formVazio();

  private get isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  private formVazio(): ParceiroForm {
    return {
      nome: '',
      cpfCnpj: '',
      telefone: '',
      limiteUsuarios: 1,
      emailAcesso: '',
      rua: '',
      cidade: '',
      estado: '',
      cep: '',
      pais: 'Brasil',
    };
  }

  ngOnInit(): void {
    this.carregar();
    void this.carregarEstados();
  }

  async carregarEstados(): Promise<void> {
    this.carregandoEstados = true;
    this.cdr.detectChanges();

    try {
      const estados = await firstValueFrom(
        this.http.get<EstadoIBGE[]>(
          'https://servicodados.ibge.gov.br/api/v1/localidades/estados?orderBy=nome',
        ),
      );

      this.estados = estados;
    } catch (error) {
      console.error('Erro ao carregar estados:', error);
      await this.alertaErro('Erro ao carregar estados', 'Não foi possível consultar os estados.');
    } finally {
      this.carregandoEstados = false;
      this.cdr.detectChanges();
    }
  }

  async carregarCidades(uf: string): Promise<void> {
    if (!uf || this.form.pais !== 'Brasil') {
      this.cidades = [];
      this.cdr.detectChanges();
      return;
    }

    this.carregandoCidades = true;
    this.cidades = [];
    this.cdr.detectChanges();

    try {
      const cidades = await firstValueFrom(
        this.http.get<CidadeIBGE[]>(
          `https://servicodados.ibge.gov.br/api/v1/localidades/estados/${uf}/municipios?orderBy=nome`,
        ),
      );

      // Evita aplicar cidades de uma consulta antiga
      if (this.form.estado !== uf) return;

      this.cidades = cidades;
    } catch (error) {
      console.error('Erro ao carregar cidades:', error);
      await this.alertaErro(
        'Erro ao carregar cidades',
        'Não foi possível consultar os municípios.',
      );
    } finally {
      if (this.form.estado === uf) {
        this.carregandoCidades = false;
      }
      this.cdr.detectChanges();
    }
  }

  async aoAlterarEstado(): Promise<void> {
    this.form.cidade = '';
    await this.carregarCidades(this.form.estado);
  }

  mascararCpfCnpj(): void {
    this.form.cpfCnpj = this.formatarCpfCnpj(this.form.cpfCnpj);
  }

  formatarCpfCnpj(documento: string): string {
    if (!documento) return '';

    const numeros = documento.replace(/\D/g, '').slice(0, 14);

    if (numeros.length <= 11) {
      return numeros
        .replace(/^(\d{3})(\d)/, '$1.$2')
        .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
        .replace(/\.(\d{3})(\d)/, '.$1-$2');
    }

    return numeros
      .replace(/^(\d{2})(\d)/, '$1.$2')
      .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/\.(\d{3})(\d)/, '.$1/$2')
      .replace(/(\d{4})(\d)/, '$1-$2');
  }

  mascararTelefone(): void {
    this.form.telefone = this.formatarTelefone(this.form.telefone);
  }

  formatarTelefone(valor: string): string {
    const numeros = (valor || '').replace(/\D/g, '').slice(0, 13);

    if (!numeros) return '';

    // Quando começar com 55, formata como telefone internacional
    if (numeros.startsWith('55')) {
      const ddi = numeros.slice(0, 2);
      const ddd = numeros.slice(2, 4);
      const celular = numeros.slice(4);

      let resultado = `+${ddi}`;

      if (ddd) {
        resultado += ` (${ddd}`;

        if (ddd.length === 2) {
          resultado += ')';
        }
      }

      if (celular) {
        resultado += ` ${celular.slice(0, 5)}`;

        if (celular.length > 5) {
          resultado += `-${celular.slice(5)}`;
        }
      }

      return resultado;
    }

    // Não acrescenta o 55 automaticamente.
    // Permite digitar sem DDI, mas a validação impedirá salvar.
    return numeros;
  }

  private normalizarTelefone(valor: string): string | null {
    const numeros = (valor || '').replace(/\D/g, '');

    // Exige exatamente 55 + DDD + celular de 9 dígitos
    if (/^55[1-9][0-9]9[0-9]{8}$/.test(numeros)) {
      return numeros;
    }

    return null;
  }

  mascararCep(): void {
    const cep = this.form.cep.replace(/\D/g, '').slice(0, 8);

    this.form.cep = cep.length > 5 ? `${cep.slice(0, 5)}-${cep.slice(5)}` : cep;
  }

  async buscarCep(): Promise<void> {
    const cep = this.form.cep.replace(/\D/g, '');

    if (cep.length !== 8 || this.buscandoCep) return;

    this.buscandoCep = true;
    this.cdr.detectChanges();

    try {
      const endereco = await firstValueFrom(
        this.http.get<EnderecoViaCEP>(`https://viacep.com.br/ws/${cep}/json/`),
      );

      if (endereco.erro) {
        await this.alertaAviso('CEP não encontrado', 'Confira o CEP informado e tente novamente.');
        return;
      }

      this.form.pais = 'Brasil';
      this.form.rua = endereco.logradouro || '';
      this.form.estado = endereco.uf;

      await this.carregarCidades(endereco.uf);

      this.form.cidade = endereco.localidade;
    } catch (error) {
      console.error('Erro ao buscar CEP:', error);

      await this.alertaErro(
        'Erro na consulta',
        'Não foi possível consultar o CEP. Preencha o endereço manualmente.',
      );
    } finally {
      this.buscandoCep = false;
      this.cdr.detectChanges();
    }
  }

  carregar(): void {
    this.carregando = true;
    this.erro = '';

    this.cdr.detectChanges();

    this.api.listar().subscribe({
      next: (resposta) => {
        this.items = resposta;
        this.carregando = false;

        this.cdr.detectChanges();
      },

      error: async (error) => {
        console.error('Erro ao carregar parceiros:', error);

        this.carregando = false;
        this.cdr.detectChanges();

        await this.alertaErro(
          'Erro ao carregar',
          this.mensagemErro(error, 'Não foi possível buscar os parceiros. Tente novamente.'),
        );
      },
    });
  }

  novo(): void {
    this.form = this.formVazio();
    this.editando = null;
    this.mostrar = true;

    this.erro = '';
    this.sucesso = '';

    this.cdr.detectChanges();
  }

  async editar(parceiro: Parceiro): Promise<void> {
    this.form = {
      nome: parceiro.nome ?? '',
      cpfCnpj: parceiro.cpfCnpj ?? '',
      telefone: parceiro.telefone ?? '',
      limiteUsuarios: parceiro.limiteUsuarios ?? 1,
      emailAcesso: parceiro.emailAcesso ?? '',
      rua: parceiro.rua ?? '',
      cidade: parceiro.cidade ?? '',
      estado: parceiro.estado ?? '',
      cep: parceiro.cep ?? '',
      pais: parceiro.pais ?? 'Brasil',
    };

    this.mascararCpfCnpj();
    this.mascararTelefone();
    this.mascararCep();

    this.editando = parceiro.parceiroUuid;
    this.mostrar = true;

    this.erro = '';
    this.sucesso = '';

    this.cdr.detectChanges();

    if (this.form.estado) {
      await this.carregarCidades(this.form.estado);
    }
  }

  cancelar(): void {
    this.mostrar = false;
    this.editando = null;
    this.form = this.formVazio();

    this.cdr.detectChanges();
  }

  async salvar(): Promise<void> {
    if (this.salvando) return;

    const nome = this.form.nome.trim();
    const cpfCnpj = this.form.cpfCnpj.replace(/\D/g, '');
    const telefone = this.normalizarTelefone(this.form.telefone);
    const limiteUsuarios = Number(this.form.limiteUsuarios);

    if (!nome) {
      await this.alertaAviso('Nome obrigatório', 'Informe o nome do parceiro.');
      return;
    }

    if (!telefone) {
      await this.alertaAviso(
        'Telefone inválido',
        'Informe o telefone completo com DDI 55 + DDD + celular de 9 dígitos. Exemplo: +55 (00) 00000-0000.',
      );
      return;
    }

    if (cpfCnpj.length !== 11 && cpfCnpj.length !== 14) {
      await this.alertaAviso(
        'CPF/CNPJ inválido',
        'Informe um CPF com 11 dígitos ou um CNPJ com 14 dígitos.',
      );
      return;
    }

    if (!this.validarCpfCnpj(cpfCnpj)) {
      const tipoDocumento = cpfCnpj.length === 11 ? 'CPF' : 'CNPJ';

      await this.alertaAviso(
        `${tipoDocumento} inválido`,
        `O ${tipoDocumento} informado não é válido. Confira os números e tente novamente.`,
      );

      return;
    }

    if (!Number.isInteger(limiteUsuarios) || limiteUsuarios < 1) {
      await this.alertaAviso(
        'Limite inválido',
        'O limite de usuários deve ser um número inteiro maior que zero.',
      );
      return;
    }

    const dados: ParceiroPayload = {
      nome,
      cpfCnpj,
      telefone,
      limiteUsuarios,
      emailAcesso: this.form.emailAcesso.trim().toLowerCase(),
      rua: this.form.rua.trim(),
      cidade: this.form.cidade.trim(),
      estado: this.form.estado.trim(),
      cep: this.form.cep.replace(/\D/g, ''),
      pais: this.form.pais.trim(),
    };

    this.salvando = true;
    this.erro = '';
    this.sucesso = '';

    this.cdr.detectChanges();

    const estavaEditando = !!this.editando;

    const requisicao = this.editando
      ? this.api.atualizar(this.editando, dados)
      : this.api.criar(dados);

    requisicao.subscribe({
      next: async () => {
        this.salvando = false;
        this.cancelar();

        this.cdr.detectChanges();
        this.carregar();

        await this.alertaSucesso(
          estavaEditando ? 'Parceiro atualizado!' : 'Parceiro cadastrado!',
          estavaEditando ? 'Parceiro atualizado com sucesso.' : 'Parceiro cadastrado com sucesso.',
        );
      },

      error: async (error) => {
        console.error('Erro ao salvar parceiro:', error);

        this.salvando = false;
        this.cdr.detectChanges();

        await this.alertaErro(
          'Erro ao salvar parceiro',
          this.mensagemErro(error, 'Não foi possível salvar o parceiro.'),
        );
      },
    });
  }

  private validarCpfCnpj(documento: string): boolean {
    const numeros = documento.replace(/\D/g, '');

    if (numeros.length === 11) {
      return this.validarCpf(numeros);
    }

    if (numeros.length === 14) {
      return this.validarCnpj(numeros);
    }

    return false;
  }

  private validarCpf(cpf: string): boolean {
    if (!/^\d{11}$/.test(cpf)) {
      return false;
    }

    // Rejeita CPFs com todos os números iguais.
    if (/^(\d)\1{10}$/.test(cpf)) {
      return false;
    }

    for (let digito = 9; digito < 11; digito++) {
      let soma = 0;

      for (let i = 0; i < digito; i++) {
        soma += Number(cpf[i]) * (digito + 1 - i);
      }

      let resto = (soma * 10) % 11;

      if (resto === 10) {
        resto = 0;
      }

      if (resto !== Number(cpf[digito])) {
        return false;
      }
    }

    return true;
  }

  private validarCnpj(cnpj: string): boolean {
    if (!/^\d{14}$/.test(cnpj)) {
      return false;
    }

    // Rejeita CNPJs com todos os números iguais.
    if (/^(\d)\1{13}$/.test(cnpj)) {
      return false;
    }

    const pesosPrimeiro = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

    const pesosSegundo = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

    let soma = 0;

    for (let i = 0; i < 12; i++) {
      soma += Number(cnpj[i]) * pesosPrimeiro[i];
    }

    let resto = soma % 11;
    const primeiroDigito = resto < 2 ? 0 : 11 - resto;

    if (primeiroDigito !== Number(cnpj[12])) {
      return false;
    }

    soma = 0;

    for (let i = 0; i < 13; i++) {
      soma += Number(cnpj[i]) * pesosSegundo[i];
    }

    resto = soma % 11;
    const segundoDigito = resto < 2 ? 0 : 11 - resto;

    return segundoDigito === Number(cnpj[13]);
  }

  async excluir(parceiro: Parceiro): Promise<void> {
    if (this.excluindo || !this.isBrowser) return;

    const ativar = !parceiro.ativo;
    const acao = ativar ? 'ativar' : 'inativar';

    const confirmacao = await Swal.fire({
      title: ativar ? 'Reativar parceiro?' : 'Inativar parceiro?',
      text: `Deseja realmente ${acao} "${parceiro.nome}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: ativar ? 'Sim, reativar' : 'Sim, inativar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: ativar ? '#064F82' : '#dc3545',
      cancelButtonColor: '#64748b',
      reverseButtons: true,
      focusCancel: true,
    });

    if (!confirmacao.isConfirmed) return;

    this.excluindo = true;
    this.erro = '';
    this.sucesso = '';

    this.cdr.detectChanges();

    Swal.fire({
      title: ativar ? 'Reativando parceiro...' : 'Inativando parceiro...',
      text: 'Processando a alteração de status.',
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => Swal.showLoading(),
    });

    this.api.alterarStatus(parceiro.parceiroUuid, ativar).subscribe({
      next: async () => {
        this.excluindo = false;

        // Atualiza imediatamente o status na tabela
        parceiro.ativo = ativar;

        this.cdr.detectChanges();
        Swal.close();

        this.carregar();

        await this.alertaSucesso(
          ativar ? 'Parceiro reativado!' : 'Parceiro inativado!',
          ativar ? 'Parceiro reativado com sucesso.' : 'Parceiro inativado com sucesso.',
        );
      },

      error: async (error) => {
        console.error(`Erro ao ${acao} parceiro:`, error);

        this.excluindo = false;
        this.cdr.detectChanges();

        Swal.close();

        await this.alertaErro(
          ativar ? 'Erro ao reativar parceiro' : 'Erro ao inativar parceiro',
          this.mensagemErro(error, `Não foi possível ${acao} o parceiro.`),
        );
      },
    });
  }

  private mensagemErro(error: any, mensagemPadrao: string): string {
    if (error?.status === 0) {
      return 'Não foi possível conectar ao servidor.';
    }

    if (error?.status === 401) {
      return 'Sua sessão expirou ou não foi autenticada. Faça login novamente.';
    }

    if (error?.status === 403) {
      return 'Você não possui permissão para realizar esta operação.';
    }

    if (error?.status === 400) {
      const mensagem = this.extrairMensagem(error);

      if (/cpf|cnpj|documento/i.test(mensagem)) {
        return 'CPF ou CNPJ inválido. Confira os números informados.';
      }

      return mensagem || 'Confira os dados informados e tente novamente.';
    }

    if (error?.status === 409) {
      return (
        this.extrairMensagem(error) ||
        'Já existe um parceiro com esses dados ou ocorreu um conflito ao salvar.'
      );
    }

    return this.extrairMensagem(error) || mensagemPadrao;
  }

  private extrairMensagem(error: any): string {
    const mensagem =
      error?.error?.message || error?.error?.mensagem || error?.error?.detail || error?.error?.erro;

    return typeof mensagem === 'string' ? mensagem.trim() : '';
  }

  private async alertaSucesso(titulo: string, mensagem: string): Promise<void> {
    if (!this.isBrowser) return;

    await Swal.fire({
      icon: 'success',
      title: titulo,
      text: mensagem,
      confirmButtonText: 'Entendi',
      confirmButtonColor: '#064F82',
      timer: 2500,
      timerProgressBar: true,
    });
  }

  private async alertaErro(titulo: string, mensagem: string): Promise<void> {
    if (!this.isBrowser) return;

    await Swal.fire({
      icon: 'error',
      title: titulo,
      text: mensagem,
      confirmButtonText: 'Fechar',
      confirmButtonColor: '#064F82',
    });
  }

  private async alertaAviso(titulo: string, mensagem: string): Promise<void> {
    if (!this.isBrowser) return;

    await Swal.fire({
      icon: 'warning',
      title: titulo,
      text: mensagem,
      confirmButtonText: 'Entendi',
      confirmButtonColor: '#064F82',
    });
  }
}
